import React, {useState, useEffect} from "react";
import {addressCommentDetails, deleteAddressStreetZip, editAddressStreetZip, flagAddressStreetZip, getUserList} from "../../services/adminServices";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import CustomUserName from "../../CustomComponents/CustomUserName";
import {fDate} from "../../utils/format-time";
import {getStatusBadge, getTypeCommentsBadge} from "../../utils/format-text";

const MyComments = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [comments, setComments] = useState([]);
	const [filteredComments, setFilteredComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterStatus, setFilterStatus] = useState("all");

	const [sortBy, setSortBy] = useState("date-desc");
	const [currentPage, setCurrentPage] = useState(1);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedComment, setSelectedComment] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingComment, setEditingComment] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
	const [editErrors, setEditErrors] = useState({});
	const [mockComments, setMockComments] = useState([]);
	const [commentInteractions, setCommentInteractions] = useState({}); // Track likes and uplifts for each comment
	const [loadingButtons, setLoadingButtons] = useState({}); // Track loading state for individual buttons
	const [page, setPage] = useState(1); // Track loading state for individual buttons
	const [pageSize, setPageSize] = useState(10); // Track loading state for individual buttons
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);
	const [totalCount, setTotalCount] = useState(0);

	const commentsPerPage = 10;

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [loadingUser, setLoadingUser] = useState(true);
	const [debounceTimer, setDebounceTimer] = useState(null);
	const [selectedUserId, setSelectedUserId] = useState(null);

	const [inputValue, setInputValue] = useState("");

	// Fetch comments data
	const getMockComments = async () => {
		const dummyComments = [];

		try {
			if (firstTimeLoad) {
				setLoading(false);
			}

			const response = await addressCommentDetails({
				pagesize: pageSize,
				page: page,
				userId: selectedUserId,
				sortBy: sortBy,
				status: filterStatus,
			});

			if (response && response.status === true && Array.isArray(response?.data?.list) && response?.data?.list?.length > 0) {
				const transformed = response?.data?.list?.map((item) => {
					const commentDate = item.comment_date ? moment(item.comment_date) : null;
					const submittedDate = item.createdAt ? moment(item.createdAt) : null;
					const likes = item?.likes || [];
					const trueCount = likes.filter((likeItem) => likeItem?.like == true).length;

					return {
						id: item.street_id,
						commentAddress: item.addresses || "Comment to Reply",
						commentText: item.description || "N/A",
						commentDate: commentDate ? commentDate.format("YYYY-MM-DD") : "",
						status: item.is_historic ? "historic" : "fresh",
						submittedDate: submittedDate ? submittedDate.format("YYYY-MM-DD") : "",
						canEdit: !item.is_historic,
						daysAgo: commentDate ? moment().diff(commentDate, "days") : 0,
						flagged: item.admin_flagged || false,
						isDemo: true,
						likeCount: trueCount,
						selfLike: item?.selfLike,
						userName: item?.user?.first_name + " " + item?.user?.last_name,
						fullAddress: item?.comment?.full_address,
						comment_type: item?.comment_type,
						item,
					};
				});

				setTotalCount(response?.data?.totalCount);
				setLoading(true);
				setMockComments(transformed);
				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			} else {
				setLoading(true);
				setMockComments(dummyComments);
			}
		} catch (error) {
			console.error("Error fetching ratings:", error);
			// Show demo ratings on error as well
			setMockComments(dummyComments);
		}
	};

	const handleSearch = async (query) => {
		if (!query || query.length < 3) {
			setOptions([]);
			return;
		}
		setLoadingUser(true);

		const timer = setTimeout(async () => {
			try {
				const res = await getUserList({search: query});
				setOptions(res?.data);
			} catch (error) {
				console.error("API error:", error);
			} finally {
				setLoadingUser(false);
			}
		}, 300);

		setDebounceTimer(timer);
	};
	useEffect(() => {
		getMockComments();
	}, [pageSize, page]);

	// Update comments when mockComments changes
	useEffect(() => {
		setComments(mockComments);
		setFilteredComments(mockComments);
		setLoadingButtons({});
		// setLoading(false);
	}, [mockComments]);

	// Filter and sort comments
	useEffect(() => {
		// setPage(1);
		// getMockComments();
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockComments();
		}
	}, [filterStatus, sortBy, selectedUserId]);

	const handleViewDetails = (comment) => {
		setSelectedComment(comment);
		setShowDetailModal(true);
	};

	const openEditModal = (comment) => {
		if (!comment.canEdit) {
			sweetAlerts("error", "This comment cannot be edited because it's older than 90 days or has been flagged.");
			return;
		}
		setEditingComment(comment);
		setEditFormData({
			id: comment.id,
			commentAddress: comment.commentAddress,

			commentDate: comment.commentDate,
			commentText: comment.commentText || "",
			canEdit: comment.status === "historic" ? false : true,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingComment(null);
		setEditFormData({});
		setEditErrors({});
		// Clear loading state for edit button
		if (editingComment) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: false}));
		}
	};

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear errors when user starts typing
		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const validateEditForm = () => {
		const newErrors = {};

		if (!editFormData.commentAddress.trim()) {
			newErrors.commentAddress = "Comment address is required";
		}

		if (!editFormData.commentDate) {
			newErrors.commentDate = "Comment date is required";
		}

		if (editFormData.overallRating === 0) {
			newErrors.overallRating = "Overall rating is required";
		}

		if (!editFormData.commentText.trim()) {
			newErrors.commentText = "Comment text is required";
		} else if (editFormData.commentText.trim().length < 10) {
			newErrors.commentText = "Comment must be at least 10 characters long";
		}

		setEditErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const getChanges = () => {
		const changes = [];

		if (editingComment.commentAddress !== editFormData.commentAddress) {
			changes.push(`Address: "${editingComment.commentAddress}" → "${editFormData.commentAddress}"`);
		}

		if (editingComment.commentDate !== editFormData.commentDate) {
			changes.push(`Comment Date: ${editingComment.commentDate} → ${editFormData.commentDate}`);
		}

		if (editingComment.commentText !== editFormData.commentText) {
			changes.push(`Comment Text: Updated`);
		}

		return changes;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		setIsSubmittingEdit(true);
		setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: true}));

		try {
			// In real app, this would be an API call like: const response = await editComment(editFormData);

			// Simulate API call
			await new Promise((resolve) => setTimeout(resolve, 1000));

			// Update the comment in the list
			setComments((prev) => prev.map((comment) => (comment.id === editFormData.id ? {...comment, ...editFormData} : comment)));

			const response = await editAddressStreetZip({
				...editFormData,
			});

			if (response && response.status === true) {
				await getMockComments();
			} else {
				sweetAlerts("error", "Failed updating comment. Please try again.");
			}

			closeEditModal();
		} catch (error) {
			console.error("Error updating comment:", error);
			sweetAlerts("error", "Failed to update comment. Please try again.");
		} finally {
			setIsSubmittingEdit(false);
			setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: false}));
		}
	};

	const calculateJobDateStatus = (jobDate) => {
		const daysDiff = moment().diff(moment(jobDate), "days");
		if (daysDiff <= 90) {
			return {status: "fresh", text: "Fresh", color: "success"};
		} else {
			return {status: "historic", text: "Historic", color: "warning"};
		}
	};

	const handleEditComment = (commentId) => {
		const comment = comments.find((c) => c.id === commentId);
		if (comment) {
			openEditModal(comment);
		}
	};

	const handleDeleteComment = async (commentId) => {
		sweetAlertQuestion("Are you sure you want to delete this comment? This action cannot be undone.").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${commentId}`]: true}));
				try {
					// In real app, this would be an API call like: const response = await deleteComment(commentId);

					// Simulate API call
					await new Promise((resolve) => setTimeout(resolve, 500));

					const response = await deleteAddressStreetZip(commentId);
					if (response && response.status === true) {
						if (mockComments.length === 1) {
							setPage(page - 1);
						}
						getMockComments();
						sweetAlerts("success", "Comment deleted successfully");
					} else {
						sweetAlerts("error", "Failed deleting rating. Please try again.");
					}
				} catch (error) {
					console.error("Error deleting comment:", error);
					sweetAlerts("error", "Failed deleting comment. Please try again.");
				} finally {
					setLoadingButtons((prev) => ({...prev, [`delete-${commentId}`]: false}));
				}
			}
		});
	};

	// Handle uplift functionality
	// const getStatusBadge = (status, flagged) => {
	// 	if (flagged) {
	// 		return <span className="badge badge-danger">Flagged</span>;
	// 	}

	// 	switch (status) {
	// 		case "fresh":
	// 			return <span className="badge badge-success">Fresh</span>;
	// 		case "historic":
	// 			return <span className="badge badge-warning">Historic</span>;
	// 		default:
	// 			return <span className="badge badge-secondary">Unknown</span>;
	// 	}
	// };

	const getTypeBadge = (type) => {
		switch (type) {
			case true:
				return <span className="badge badge-success">📍 Zipcode</span>;
			default:
				return <span className="badge badge-warning">🏘️ Street</span>;
		}
	};

	const paginate = (pageNumber) => setPage(pageNumber);

	const handleFlagged = async (ratingId) => {
		setLoadingButtons((prev) => ({...prev, [`flag-${ratingId}`]: true}));
		try {
			const response = await flagAddressStreetZip({
				id: ratingId,
			});

			if (response && response.status === true) {
				await getMockComments();
			} else {
				sweetAlerts("error", "Failed flagging comment. Please try again.");
			}
		} catch (error) {
			sweetAlerts("error", "Error flagging comment. Please try again.");
		} finally {
			setLoadingButtons((prev) => ({...prev, [`flag-${ratingId}`]: false}));
		}
	};

	const truncateWords = (text, maxChars) => {
		if (text.length <= maxChars) return text;
		return text.substring(0, maxChars) + "...";
	};

	// if (loading) {
	// 	return <CustomLoader />;
	// }

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header">
				<h2>Comments</h2>
				<p>View and manage all your comments ({totalLength} total)</p>
			</div>

			{/* Filters and Search */}
			<div className="ratings-controls">
				{/* <div className="search-section">
					<div className="search-input-wrapper">
						<input type="text" className="search-input" placeholder="Search by address or comment..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
						<svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
							<path
								d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
				</div> */}

				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="fresh">Fresh Comments</option>
						<option value="historic">Historic Comments</option>
						<option value="flagged">Flagged Comments</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
						{/* <option value="address">Address A-Z</option> */}
					</select>

					<Autocomplete
						open={open}
						onOpen={() => setOpen(true)}
						onClose={() => setOpen(false)}
						options={options}
						getOptionLabel={(option) => option?.first_name + " " + option?.last_name || ""}
						loading={loadingUser}
						inputValue={inputValue}
						onInputChange={(e, value, reason) => {
							setInputValue(value);
							handleSearch(value);

							// Clear selection when user types or backspaces
							if (reason === "input") {
								const selectedUser = selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) : null;
								const selectedUserName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : "";

								// If the current input doesn't match the selected user's name, clear selection
								if (value !== selectedUserName) {
									setSelectedUserId(null);
								}
							}
						}}
						onChange={(e, value) => {
							if (value) {
								setSelectedUserId(value.user_id);
								setInputValue(value.first_name + " " + value.last_name);
							} else {
								setSelectedUserId(null);
								setInputValue("");
							}
						}}
						value={selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) || null : null}
						clearIcon={<i className="fa-2xs fas fa-close" />}
						popupIcon={null}
						autoComplete
						includeInputInList
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="User Name"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "10px",
										backgroundColor: "#fff",
										"& .MuiOutlinedInput-input": {
											padding: "10px 2px",
											height: 3,
										},
										"& fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&:hover fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#28a745",
											borderWidth: "2px",
											boxShadow: "0 0 0 .2rem #22c55e40",
										},
									},
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: <>{params.InputProps.endAdornment}</>,
								}}
							/>
						)}
						sx={{width: 250}}
					/>
				</div>
			</div>

			{/* Comments List */}

			{loading ? (
				<div className="admin-ratings-container">
					{filteredComments.length === 0 ? (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Address</th>
											<th className="align-middle address-cell">Street/Zipcode</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Job Date</th>
											<th className="align-middle address-cell">Comment Type</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr className="rating-row">
											<td className="address-cell text-start" colSpan={8}>
												No comments found
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Street/Zipcode</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Job Date</th>
											<th className="align-middle address-cell">Comment Type</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredComments.map((comment) => (
											<tr key={comment.id} className={`comment-row ${comment.flagged ? "flagged" : ""}`} data-id={comment.id}>
												<td className="align-middle address-cell cursor-pointer" title={comment.commentAddress} onClick={() => handleViewDetails(comment)}>
													{truncateWords(comment.commentAddress, 50)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(comment)} title={comment.userName}>
													<CustomUserName user={comment?.item?.user} />
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(comment)}>
													{fDate(comment.commentDate)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(comment)}>
													{getTypeCommentsBadge(comment?.comment_type)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(comment)}>
													{getStatusBadge(comment?.item?.is_historic, comment?.admin_flagged)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(comment)}>
													{fDate(comment.submittedDate)}
												</td>
												<td className="align-middle actions-cell">
													<div className="table-actions mb-0 justify-content-end flex-row">
														<button className="btn-icon" onClick={() => handleFlagged(comment.id)} title="Flag Rating" disabled={loadingButtons[`flag-${comment.id}`]}>
															{loadingButtons[`flag-${comment.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																	<path
																		d="M10.29 3.86L1.82 18C1.64 18.37 1.54 18.78 1.54 19.2C1.54 19.62 1.64 20.03 1.82 20.4C2 20.77 2.26 21.09 2.59 21.33C2.92 21.57 3.31 21.73 3.72 21.8C4.13 21.87 4.55 21.85 4.95 21.75C5.35 21.65 5.72 21.47 6.04 21.22L12 17.27L17.96 21.22C18.28 21.47 18.65 21.65 19.05 21.75C19.45 21.85 19.87 21.87 20.28 21.8C20.69 21.73 21.08 21.57 21.41 21.33C21.74 21.09 22 20.77 22.18 20.4C22.36 20.03 22.46 19.62 22.46 19.2C22.46 18.78 22.36 18.37 22.18 18L13.71 3.86C13.53 3.49 13.27 3.17 12.94 2.93C12.61 2.69 12.22 2.53 11.81 2.46C11.4 2.39 10.98 2.41 10.58 2.51C10.18 2.61 9.81 2.79 9.49 3.04C9.17 3.29 8.91 3.61 8.73 3.98C8.55 4.35 8.45 4.76 8.45 5.18C8.45 5.6 8.55 6.01 8.73 6.38L10.29 3.86Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path d="M12 9V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																</svg>
															)}
														</button>
														<button className="btn-icon" onClick={() => handleViewDetails(comment)} title="View Details">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
														</button>
														<button className="btn-icon btn-edit" onClick={() => handleEditComment(comment.id)} title="Edit Comment" disabled={loadingButtons[`edit-${comment.id}`]}>
															{loadingButtons[`edit-${comment.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path
																		d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path
																		d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
														<button className="btn-icon btn-delete" onClick={() => handleDeleteComment(comment.id)} title="Delete Comment" disabled={loadingButtons[`delete-${comment.id}`]}>
															{loadingButtons[`delete-${comment.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path
																		d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							<>
								<div className="pagination justify-content-between mt-0 pt-0 pe-3 px-3">
									<div className="pagination-info d-flex justify-content-between align-items-center gap-3">
										<span className="pagination-info-text">
											Showing {page * pageSize - pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
										</span>
										<div>
											<select className="filter-select" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
												<option value="10">10</option>
												<option value="20">20</option>
												<option value="25">25</option>
												<option value="50">50</option>
												<option value="100">100</option>
											</select>
										</div>
									</div>
									<div className="d-flex justify-content-end pb-3 pt-3 pe-2">
										<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
											Previous
										</button>

										<div className="pagination-numbers">
											{[...Array(totalPages)].map((_, index) => {
												const ipage = index + 1;
												if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
													return (
														<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
															{ipage}
														</button>
													);
												} else if (ipage === page - 2 || ipage === page + 2) {
													return (
														<span key={ipage} className="pagination-ellipsis">
															...
														</span>
													);
												}
												return null;
											})}
										</div>

										<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
											Next
										</button>
									</div>
								</div>
							</>
						</div>
					)}
				</div>
			) : (
				<div className="admin-ratings-container">
					<div className="ratings-table-container">
						<div className="table-responsive sticky-table">
							<table className="ratings-table">
								<thead>
									<tr>
										<th className="align-middle address-cell">Street/Zipcode</th>
										<th className="align-middle address-cell">User Name</th>
										<th className="align-middle address-cell">Job Date</th>
										<th className="align-middle address-cell">Comment Type</th>
										{/* <th className="align-middle address-cell">Status</th> */}
										{/* <th className="align-middle address-cell">Submitted</th> */}
										<th className="actions-cell text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr className="rating-row">
										<td className="address-cell text-center" colSpan={6}>
											<CustomLoader />
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{/* Detail Modal */}
			{showDetailModal && selectedComment && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Comment Details</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Comment Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Address:</span>
										<span className="detail-value">{selectedComment.fullAddress}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Street/Zipcode:</span>
										<span className="detail-value">{selectedComment.commentAddress}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Comment Date:</span>
										<span className="detail-value">
											{moment(selectedComment.commentDate).format("MMM DD, YYYY")}
											<span className="days-ago"> ({selectedComment.daysAgo} days ago)</span>
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Comment Type:</span>
										<span className="detail-value">{getTypeBadge(selectedComment.comment_type)}</span>
									</div>
									{/* <div className="detail-item">
										<span className="detail-label">Overall Rating:</span>
									</div> */}
								</div>
							</div>

							<div className="detail-section">
								<h4>Comment</h4>
								<div className="job-description-full">
									<p>{selectedComment.commentText}</p>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{showEditModal && editingComment && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" style={{maxWidth: "600px"}} onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Edit Comment</h3>
							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-group">
								<label className="form-label">Comment Address</label>
								<input
									type="text"
									className={`form-input ${editErrors.commentAddress ? "error" : ""}`}
									value={editFormData.commentAddress}
									onChange={(e) => handleEditInputChange("commentAddress", e.target.value)}
									readOnly
								/>
								{editErrors.commentAddress && <span className="error-text">{editErrors.commentAddress}</span>}
							</div>

							<div className="form-group">
								<label className="form-label">Comment Date</label>
								<input
									type="date"
									onClick={(e) => {
										e.target.showPicker && e.target.showPicker();
									}}
									className={`form-input ${editErrors.commentDate ? "error" : ""}`}
									value={editFormData.commentDate}
									onChange={(e) => handleEditInputChange("commentDate", e.target.value)}
								/>
								{editErrors.commentDate && <span className="error-text">{editErrors.commentDate}</span>}
							</div>

							<div className="form-group">
								<label className="form-label">Comment Text</label>
								<textarea
									className={`form-textarea ${editErrors.commentText ? "error" : ""}`}
									value={editFormData.commentText}
									onChange={(e) => handleEditInputChange("commentText", e.target.value)}
									rows="4"
									placeholder="Describe your experience with this job..."
								/>
								{editErrors.commentText && <span className="error-text">{editErrors.commentText}</span>}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Updating...
									</>
								) : (
									"Update Comment"
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MyComments;
